import React, { useEffect, useState } from 'react';
import {
  useColorModeValue } from "./ui/color-mode";
import { useNavigate,
  useParams } from 'react-router-dom';
import {
  Box,
  VStack,
  HStack,
  Text,
  Flex,
  Spinner,
  Badge,
  Input,
  Heading,
  Card,
  Icon,
} from '@chakra-ui/react';
import { Divider } from '@/components/ui/chakra-v2-compat';
import { FaLock, FaUsers } from 'react-icons/fa';
import { encuestaGrupoAPI } from '../services/api';

/**
 * Thin public identify page for group WhatsApp encuesta de asistencia.
 * After choosing a musician, navigates to /encuesta/:token.
 */
export default function EncuestaGrupoPublica() {
  const { shareToken } = useParams();
  const navigate = useNavigate();

  const [mode, setMode] = useState('loading'); // loading | identify | closed | error
  const [meta, setMeta] = useState(null);
  const [musicos, setMusicos] = useState([]);
  const [searchFilter, setSearchFilter] = useState('');
  const [choosing, setChoosing] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const bgGradient = useColorModeValue(
    'linear(to-b, purple.50, gray.50, white)',
    'linear(to-b, gray.900, gray.800)',
  );
  const cardBg = useColorModeValue('white', 'gray.700');
  const accentColor = useColorModeValue('purple.600', 'purple.300');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  useEffect(() => {
    if (!shareToken) {
      setMode('error');
      setErrorMsg('Enlace no válido.');
      return;
    }
    (async () => {
      try {
        const { data } = await encuestaGrupoAPI.getMeta(shareToken);
        setMeta(data);
        if (data.estado === 'CERRADA') {
          setMode('closed');
          return;
        }
        setMusicos(data.musicos || []);
        setMode('identify');
      } catch (err) {
        setMode('error');
        setErrorMsg(
          err.response?.status === 404
            ? 'Encuesta no encontrada o el enlace ha expirado.'
            : 'Error al cargar la encuesta. Inténtalo de nuevo.',
        );
      }
    })();
  }, [shareToken]);

  const filteredMusicos = musicos.filter((m) => {
    const term = searchFilter.toLowerCase();
    const nombre = (m.nombre || '').toLowerCase();
    const apellidos = (m.apellidos || '').toLowerCase();
    return nombre.includes(term) || apellidos.includes(term);
  });

  const handleElegir = async (musico) => {
    if (!shareToken || !musico?.id || choosing) return;
    setChoosing(true);
    try {
      const { data } = await encuestaGrupoAPI.elegir(shareToken, musico.id);
      if (!data?.token) throw new Error('Sin token');
      navigate(`/encuesta/${data.token}`, { replace: true });
    } catch (err) {
      setMode('error');
      setErrorMsg(
        err.response?.data?.message
        || (err.response?.status === 400
          ? 'La encuesta está cerrada.'
          : 'No se pudo identificar. Inténtalo de nuevo.'),
      );
      setChoosing(false);
    }
  };

  const pageShell = (content) => (
    <Box minH="100vh" bgGradient={bgGradient}>
      <Flex direction="column" minH="100vh">
        <Box flex="1" py={{ base: 6, md: 10 }} px={{ base: 4, md: 6 }}>
          <Box maxW="700px" mx="auto">
            {content}
          </Box>
        </Box>
        <Flex justify="center" py={4} opacity={0.5}>
          <Text fontSize="xs" color="gray.500">
            Powered by <strong>Cadencya</strong>
          </Text>
        </Flex>
      </Flex>
    </Box>
  );

  if (mode === 'loading' || choosing) {
    return pageShell(
      <Flex justify="center" align="center" minH="300px">
        <VStack gap={4}>
          <Spinner size="xl" color="purple.500" borderWidth="4px" />
          <Text color="gray.500">
            {choosing ? 'Abriendo tu encuesta…' : 'Cargando encuesta…'}
          </Text>
        </VStack>
      </Flex>,
    );
  }

  if (mode === 'error') {
    return pageShell(
      <Card.Root bg={cardBg} shadow="lg" borderRadius="xl" overflow="hidden">
        <Card.Body>
          <VStack gap={5} py={6} textAlign="center">
            <Icon boxSize={12} color="red.400" asChild><FaLock /></Icon>
            <Heading size="md" color="red.500">Enlace no disponible</Heading>
            <Text color="gray.600">{errorMsg}</Text>
          </VStack>
        </Card.Body>
      </Card.Root>,
    );
  }

  if (mode === 'closed') {
    return pageShell(
      <Card.Root bg={cardBg} shadow="lg" borderRadius="xl" overflow="hidden">
        <Card.Body>
          <VStack gap={5} py={6} textAlign="center">
            <Icon boxSize={12} color="orange.400" asChild><FaLock /></Icon>
            <Heading size="md">Esta encuesta está cerrada</Heading>
            <Text color="gray.500">
              Ya no se aceptan más respuestas. Gracias por tu interés.
            </Text>
          </VStack>
        </Card.Body>
      </Card.Root>,
    );
  }

  const titulo = meta?.actuacion?.nombre || 'Encuesta de asistencia';
  const banda = meta?.actuacion?.banda?.nombre;

  return pageShell(
    <>
      <Card.Root bg={cardBg} shadow="lg" borderRadius="xl" overflow="hidden" mb={6}>
        <Box bgGradient="linear(to-r, purple.500, purple.700)" px={6} py={5}>
          <HStack gap={3} justify="space-between" align="start">
            <HStack gap={3}>
              <Icon boxSize={6} color="white" asChild><FaUsers /></Icon>
              <Box>
                <Heading size="md" color="white" lineClamp={2}>{titulo}</Heading>
                {banda ? (
                  <Text fontSize="sm" color="purple.100" mt={1}>{banda}</Text>
                ) : null}
              </Box>
            </HStack>
            <Badge colorPalette="green" alignSelf="center">ABIERTA</Badge>
          </HStack>
        </Box>
      </Card.Root>

      <Card.Root bg={cardBg} shadow="md" borderRadius="xl" overflow="hidden">
        <Card.Body>
          <VStack gap={4} align="stretch">
            <Heading size="sm" color={accentColor}>
              Identifícate para continuar
            </Heading>
            <Text fontSize="sm" color="gray.500">
              Selecciona tu nombre de la lista para responder a la encuesta de asistencia.
            </Text>

            <Input
              placeholder="Buscar por nombre…"
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              size="md"
              borderRadius="lg" />

            <Box
              maxH="320px"
              overflowY="auto"
              border="1px solid"
              borderColor={borderColor}
              borderRadius="lg"
            >
              {filteredMusicos.length === 0 ? (
                <Text textAlign="center" py={6} color="gray.400" fontSize="sm">
                  No se encontraron músicos
                </Text>
              ) : (
                <VStack gap={0} align="stretch">{filteredMusicos.map((m) => {
                    const nombre = m.nombre || '';
                    const apellidos = m.apellidos || '';
                    return (
                      <Box
                        key={m.id}
                        px={4}
                        py={3}
                        cursor="pointer"
                        _hover={{ bg: 'purple.50' }}
                        transition="background 0.15s"
                        onClick={() => handleElegir(m)}
                      >
                        <HStack gap={3}>
                          <Flex
                            w="40px"
                            h="40px"
                            borderRadius="full"
                            bg="purple.100"
                            align="center"
                            justify="center"
                            fontSize="sm"
                            fontWeight="bold"
                            color="purple.600"
                          >
                            {(nombre || '?')[0]}
                          </Flex>
                          <Text fontWeight="medium" fontSize="sm">
                            {nombre} {apellidos}
                          </Text>
                        </HStack>
                      </Box>
                    );
                  })}</VStack>
              )}
            </Box>
          </VStack>
        </Card.Body>
      </Card.Root>
    </>,
  );
}
